// v3.5.79: Schedule of Services preview page.
// Admin-only route at /admin/sos/preview.
//
// Renders the current schedule by reading active rows from
// schedule_of_services and grouping them by category. Output mirrors
// the format the law firm is familiar with (EpiOn-style two-column
// layout: part_number on the left, content blocks on the right).
//
// Header banner shows:
//   - The current version (from sos_metadata)
//   - A warning if any active rows are still review_status='needs_review'
//
// This page is intended for internal review by Ferrum staff before
// the schedule is sent to the law firm. The publicly-consumed version
// will be served from a Netlify function in v3.5.80.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

export default function SoSPreviewPage() {
  const navigate = useNavigate();
  const [rows, setRows]         = useState([]);
  const [meta, setMeta]         = useState({});
  const [loading, setLoading]   = useState(true);
  const [includeDrafts, setIncludeDrafts] = useState(false);

  useEffect(() => {
    Promise.all([
      supabase.from('schedule_of_services').select('*').order('sort_order'),
      supabase.from('sos_metadata').select('*'),
    ]).then(([rowRes, metaRes]) => {
      setRows(rowRes.data || []);
      const m = {};
      (metaRes.data || []).forEach(r => { m[r.key] = r.value; });
      setMeta(m);
      setLoading(false);
    });
  }, []);

  // Active rows render by default; toggle to also include drafts (useful
  // when reviewing pre-publication content)
  const visibleRows = rows.filter(r =>
    includeDrafts ? r.status !== 'retired' : r.status === 'active'
  );

  // Group by category preserving sort_order
  const byCategory = {};
  visibleRows.forEach(r => {
    if (!byCategory[r.category]) byCategory[r.category] = [];
    byCategory[r.category].push(r);
  });
  // Categories ordered by the smallest sort_order in each
  const categories = Object.keys(byCategory).sort((a, b) =>
    byCategory[a][0].sort_order - byCategory[b][0].sort_order
  );

  const pendingReview = rows.filter(r =>
    r.status === 'active' && r.review_status === 'needs_review'
  ).length;

  if (loading) {
    return (
      <div style={S.loader}>Loading schedule…</div>
    );
  }

  return (
    // v3.5.79.4: Layout wraps routed pages in `overflow: hidden`, so this
    // page must claim height + handle its own scroll. Without these styles
    // the preview is trapped in a non-scrolling parent and content past
    // the fold is unreachable.
    <div style={S.shell}>
      <div style={S.scroller}>
      <div style={S.page}>
      {/* Toolbar — print-hidden so the printed schedule looks clean */}
      <div className="no-print" style={S.toolbar}>
        <button onClick={() => {
          // The preview was opened via window.open('_blank'), so the user
          // still has the Schedule of Services admin tab open in the prior
          // window. Closing this tab takes them right back to it. Browsers
          // sometimes block window.close on tabs they don't think this
          // script opened — fall back to navigation in that case.
          try {
            window.close();
            // If close() didn't actually close (browser blocked it), fall through
            setTimeout(() => navigate('/admin'), 50);
          } catch {
            navigate('/admin');
          }
        }} style={S.backBtn}>← Close Preview</button>
        <div style={{ flex: 1 }} />
        <label style={S.checkboxLabel}>
          <input type="checkbox" checked={includeDrafts} onChange={e => setIncludeDrafts(e.target.checked)} />
          Include drafts
        </label>
        <button onClick={() => window.print()} style={S.printBtn}>🖨 Print / Save PDF</button>
      </div>

      {/* Header — version, generation date, warning banner */}
      <div style={S.header}>
        <h1 style={S.title}>Schedule of Services</h1>
        <div style={S.subtitle}>
          Version <strong>{meta.current_version || 'v1-draft'}</strong>
          {' · '}
          Generated {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          {meta.last_sent_to_attorney && (
            <>{' · '}Last sent to attorney: {meta.last_sent_to_attorney}</>
          )}
          {meta.last_approved_date && (
            <>{' · '}Last approved: {meta.last_approved_date}</>
          )}
        </div>
        {pendingReview > 0 && (
          <div className="no-print" style={S.warningBanner}>
            ⚠ This schedule includes {pendingReview} active row{pendingReview === 1 ? '' : 's'} still flagged
            <strong> needs_review</strong>. Review and approve in the Schedule of Services admin tab
            before sending to the attorney.
          </div>
        )}
        {includeDrafts && (
          <div className="no-print" style={S.draftBanner}>
            Showing drafts in addition to active rows. Drafts are marked with a [DRAFT] tag and would not appear in the published schedule.
          </div>
        )}
      </div>

      {/* Body — categories with their rows */}
      {categories.length === 0 && (
        <div style={S.emptyState}>
          No active rows to display. Mark rows as <strong>active</strong> in the admin tab to see them here.
        </div>
      )}

      {categories.map(cat => (
        <section key={cat} style={S.category}>
          <h2 style={S.categoryHeading}>{cat}</h2>
          {byCategory[cat].map(row => (
            <RowRenderer key={row.id} row={row} />
          ))}
        </section>
      ))}

      {/* Footer */}
      <footer style={S.footer}>
        Schedule of Services · Ferrum Technology Services · {meta.attorney_firm || 'Scott & Scott LLP'}
      </footer>

      {/* Print stylesheet — hide toolbar, expand for paper */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { margin: 0.5in; }
          body { font-size: 10pt; }
        }
      `}</style>
      </div>
      </div>
    </div>
  );
}

function RowRenderer({ row }) {
  const isDraft = row.status === 'draft';
  const included     = parseJsonSafe(row.sos_included);
  const assumptions  = parseJsonSafe(row.sos_assumptions);
  const requirements = parseJsonSafe(row.sos_requirements);
  const reviewBadge  = row.review_status === 'approved' ? null
                     : row.review_status === 'sent_to_attorney' ? '[SENT]'
                     : '[NEEDS REVIEW]';

  return (
    <div style={S.row}>
      <div style={S.rowLeft}>
        <div style={S.partNumber}>{row.part_number}</div>
        {isDraft && <div style={S.draftTag}>[DRAFT]</div>}
        {reviewBadge && row.status === 'active' && <div style={S.reviewBadge}>{reviewBadge}</div>}
      </div>
      <div style={S.rowRight}>
        <h3 style={S.displayName}>{row.display_name}</h3>
        {row.sos_summary && (
          <p style={S.summary}>{row.sos_summary}</p>
        )}
        {included.length > 0 && (
          <Section title="What's Included" items={included} />
        )}
        {assumptions.length > 0 && (
          <Section title="Assumptions and Exceptions" items={assumptions} />
        )}
        {requirements.length > 0 && (
          <Section title="Customer Requirements" items={requirements} />
        )}
      </div>
    </div>
  );
}

function Section({ title, items }) {
  return (
    <div style={S.section}>
      <div style={S.sectionTitle}>{title}</div>
      <ul style={S.bulletList}>
        {items.map((item, i) => (
          <li key={i} style={S.bullet}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

// JSONB columns come back as already-parsed arrays from supabase-js.
// Defensive against the edge case of a string row (e.g., legacy data).
function parseJsonSafe(v) {
  if (Array.isArray(v)) return v;
  if (typeof v === 'string') {
    try { return JSON.parse(v); } catch { return []; }
  }
  return [];
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = {
  // v3.5.79.4: shell claims the full height of Layout's content area;
  // scroller handles vertical overflow. Required because Layout uses
  // overflow:hidden on its content slot.
  shell: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    background: '#f9fafb',
  },
  scroller: {
    flex: 1,
    overflow: 'auto',
  },
  page: {
    maxWidth: 880,
    margin: '0 auto',
    padding: '32px 48px 80px',
    background: 'white',
    fontFamily: '-apple-system, BlinkMacSystemFont, system-ui, sans-serif',
    color: '#111',
    lineHeight: 1.5,
  },
  loader: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: '100vh', fontFamily: 'system-ui', color: '#6b7280', fontSize: 14,
  },
  toolbar: {
    display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24,
    padding: '8px 0', borderBottom: '1px solid #e5e7eb',
  },
  backBtn: {
    padding: '6px 12px', background: '#f3f4f6', color: '#374151',
    border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, cursor: 'pointer',
  },
  printBtn: {
    padding: '6px 14px', background: '#0f1e3c', color: 'white',
    border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  checkboxLabel: {
    display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#374151',
  },
  header: {
    marginBottom: 36, paddingBottom: 16, borderBottom: '2px solid #0f1e3c',
  },
  title: {
    fontSize: 24, fontWeight: 700, color: '#0f1e3c', margin: 0,
  },
  subtitle: {
    fontSize: 12, color: '#6b7280', marginTop: 6,
  },
  warningBanner: {
    marginTop: 12, padding: '10px 14px', background: '#fef3c7',
    border: '1px solid #fcd34d', borderRadius: 4, fontSize: 12, color: '#78350f',
  },
  draftBanner: {
    marginTop: 12, padding: '10px 14px', background: '#dbeafe',
    border: '1px solid #93c5fd', borderRadius: 4, fontSize: 12, color: '#1e3a8a',
  },
  emptyState: {
    textAlign: 'center', color: '#6b7280', fontSize: 13, padding: '40px 20px',
  },
  category: {
    marginBottom: 32,
  },
  categoryHeading: {
    fontSize: 16, fontWeight: 700, color: '#0f1e3c',
    background: '#f3f4f6', padding: '8px 12px', borderRadius: 4,
    margin: '0 0 14px',
  },
  row: {
    display: 'flex', gap: 18, marginBottom: 22, paddingBottom: 16,
    borderBottom: '1px solid #f3f4f6', pageBreakInside: 'avoid',
  },
  rowLeft: {
    flexShrink: 0, width: 130, paddingTop: 4,
  },
  partNumber: {
    fontSize: 11, fontWeight: 700, color: '#374151', letterSpacing: 0.4,
  },
  draftTag: {
    display: 'inline-block', marginTop: 4, padding: '2px 6px',
    background: '#dbeafe', color: '#1e3a8a',
    fontSize: 9, fontWeight: 700, borderRadius: 3,
  },
  reviewBadge: {
    display: 'inline-block', marginTop: 4, padding: '2px 6px',
    background: '#fef3c7', color: '#78350f',
    fontSize: 9, fontWeight: 700, borderRadius: 3,
  },
  rowRight: {
    flex: 1,
  },
  displayName: {
    fontSize: 14, fontWeight: 700, color: '#0f1e3c',
    margin: '0 0 6px',
  },
  summary: {
    fontSize: 12, color: '#374151', margin: '0 0 10px',
  },
  section: {
    marginBottom: 8,
  },
  sectionTitle: {
    fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 4,
    textTransform: 'uppercase', letterSpacing: 0.4,
  },
  bulletList: {
    margin: 0, paddingLeft: 20,
  },
  bullet: {
    fontSize: 12, color: '#374151', marginBottom: 3, lineHeight: 1.5,
  },
  footer: {
    marginTop: 40, paddingTop: 16, borderTop: '1px solid #e5e7eb',
    textAlign: 'center', fontSize: 11, color: '#9ca3af',
  },
};
